import { useConnect, useDisconnect, useAccount } from "wagmi";
import { useMormSigner } from "@mormlayer/hooks";
import { useState } from "react";
import "./WalletConnect.css";

// Wallet detection (aligned with mormlayer FunctionalSigner / AgentDelegation)
function useWalletDetection() {
  const isMetaMaskAvailable =
    typeof window !== "undefined" && !!(window as Window & { ethereum?: { isMetaMask?: boolean } }).ethereum?.isMetaMask;
  const isPhantomAvailable =
    typeof window !== "undefined" &&
    (!!(window as Window & { phantom?: { ethereum?: unknown } }).phantom?.ethereum ||
      !!(window as Window & { ethereum?: { isPhantom?: boolean } }).ethereum?.isPhantom);
  const isOneKeyAvailable =
    typeof window !== "undefined" &&
    (!!(window as Window & { $onekey?: { ethereum?: unknown } }).$onekey?.ethereum ||
      !!(window as Window & { ethereum?: { isOneKey?: boolean } }).ethereum?.isOneKey);

  const detectedWallet =
    typeof window === "undefined"
      ? null
      : (window as Window & { phantom?: { ethereum?: unknown }; ethereum?: { isPhantom?: boolean; isOneKey?: boolean; isMetaMask?: boolean }; $onekey?: { ethereum?: unknown } }).phantom?.ethereum ||
          (window as Window & { ethereum?: { isPhantom?: boolean } }).ethereum?.isPhantom
        ? "Phantom"
        : (window as Window & { $onekey?: { ethereum?: unknown }; ethereum?: { isOneKey?: boolean } }).$onekey?.ethereum ||
            (window as Window & { ethereum?: { isOneKey?: boolean } }).ethereum?.isOneKey
          ? "OneKey"
          : (window as Window & { ethereum?: { isMetaMask?: boolean } }).ethereum?.isMetaMask
            ? "MetaMask"
            : null;

  return {
    isMetaMaskAvailable,
    isPhantomAvailable,
    isOneKeyAvailable,
    hasAnyWallet: isMetaMaskAvailable || isPhantomAvailable || isOneKeyAvailable,
    detectedWallet,
  };
}

export function WalletConnect() {
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { address, isConnected } = useAccount();
  const { wasmStatus, isReady: wasmReady } = useMormSigner();
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    isMetaMaskAvailable,
    isPhantomAvailable,
    isOneKeyAvailable,
    hasAnyWallet,
    detectedWallet,
  } = useWalletDetection();

  const injectedConnector = connectors.find((c) => c.id === "injected");
  const shortAddress = address
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : "";

  type WalletWindow = Window & {
    ethereum?: { request: (args: { method: string }) => Promise<unknown>; isPhantom?: boolean; isOneKey?: boolean };
    phantom?: { ethereum?: { request: (args: { method: string }) => Promise<unknown> }; request?: (args: { method: string }) => Promise<unknown> };
    $onekey?: { ethereum?: { request: (args: { method: string }) => Promise<unknown> } };
  };
  const getProvider = (wallet: "MetaMask" | "Phantom" | "OneKey") => {
    const win = typeof window !== "undefined" ? (window as unknown as WalletWindow) : null;
    if (!win) return null;
    if (wallet === "Phantom") {
      return win.phantom?.ethereum ?? (win.ethereum?.isPhantom ? win.ethereum : null);
    }
    if (wallet === "OneKey") {
      return win.$onekey?.ethereum ?? win.ethereum ?? null;
    }
    return win.ethereum ?? null;
  };

  const connectWithProvider = async (wallet: "MetaMask" | "Phantom" | "OneKey") => {
    const provider = getProvider(wallet);
    if (!provider || !("request" in provider)) {
      alert(`${wallet} is not available. Please install the ${wallet} extension.`);
      return;
    }
    try {
      await (provider as { request: (args: { method: string }) => Promise<string[]> }).request({
        method: "eth_requestAccounts",
      });
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (err: unknown) {
      const error = err as { code?: number; message?: string };
      if (error.code === 4001) {
        // User rejected
        return;
      }
      console.error(`Connect ${wallet}:`, error);
      alert(`Failed to connect: ${error.message ?? "Unknown error"}`);
    }
  };

  const handleConnectMetaMask = () => connectWithProvider("MetaMask");
  const handleConnectPhantom = () => connectWithProvider("Phantom");
  const handleConnectOneKey = () => connectWithProvider("OneKey");

  return (
    <div className="wallet-connect">
      {!isConnected ? (
        <div className="wallet-connect-buttons">
          {isMetaMaskAvailable && (
            <button
              type="button"
              className="wallet-connect-btn primary"
              onClick={handleConnectMetaMask}
              disabled={isConnecting || !wasmReady}
            >
              {isConnecting ? "Connecting…" : "Connect MetaMask"}
            </button>
          )}
          {isPhantomAvailable && (
            <button
              type="button"
              className="wallet-connect-btn primary"
              onClick={handleConnectPhantom}
              disabled={isConnecting || !wasmReady}
            >
              {isConnecting ? "Connecting…" : "Connect Phantom"}
            </button>
          )}
          {isOneKeyAvailable && (
            <button
              type="button"
              className="wallet-connect-btn primary"
              onClick={handleConnectOneKey}
              disabled={isConnecting || !wasmReady}
            >
              {isConnecting ? "Connecting…" : "Connect OneKey"}
            </button>
          )}
          {!hasAnyWallet && (
            <span className="wallet-connect-unavailable">
              No supported wallet detected. Install MetaMask, Phantom, or OneKey.
            </span>
          )}
        </div>
      ) : (
        <div className="wallet-connect-dropdown-wrap">
          <button
            type="button"
            className="wallet-connect-btn connected"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <span className="wallet-dot" />
            {shortAddress}
          </button>
          {showDropdown && (
            <>
              <div
                className="wallet-dropdown-backdrop"
                onClick={() => setShowDropdown(false)}
                aria-hidden
              />
              <div className="wallet-dropdown">
                {detectedWallet && (
                  <div className="wallet-dropdown-meta">
                    Wallet: {detectedWallet}
                  </div>
                )}
                <div className="wallet-dropdown-address" title={address ?? undefined}>
                  {address}
                </div>
                <div className="wallet-dropdown-meta">
                  WASM: {wasmStatus.loaded ? "Ready" : wasmStatus.loading ? "Loading" : "Error"}
                </div>
                <button
                  type="button"
                  className="wallet-disconnect-btn"
                  onClick={() => {
                    disconnect();
                    setShowDropdown(false);
                  }}
                >
                  Disconnect
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
